import { attackPose, attackStyle, attackTiming, drawAttack, hitSprite, type AttackCue } from "../art/battleMotion.ts";
import { enterAge, settleT } from "../art/craftFeel.ts";
import { card, commandDeck, DECK, foePlate, listDeck, messageDeck, moveDeck, youPlate, type MoveRow } from "../art/battleHud.ts";
import { drawStage, FOE_PAD, todFor, YOU_PAD } from "../art/battleStage.ts";
import { creatureSprite } from "../art/creatures.ts";
import { UI, withA } from "../art/pal.ts";
import { tinSprite, type TinKind } from "../art/tin.ts";
import { ITEMS, itemHeal, type ItemId } from "../data/items.ts";
import { GRASS_QUIET, WAKE, ZONES, zoneAt, type ZoneId } from "../data/map.ts";
import { nextDuel } from "../data/routePlan.ts";
import { moveById, moveEffectLabel, type MoveDef } from "../data/moves.ts";
import { speciesById } from "../data/species.ts";
import { CIRCUIT_IDS, TRAINERS, encorePrize, isCircuitTrainer, trainerEncounter, trainerLock, type TrainerDef } from "../data/trainers.ts";
import { weekOf } from "../game/goals.ts";
import { bestTypesAgainst, TYPE_META, typeMod } from "../data/types.ts";
import { STATUS_META, type StatusId } from "../data/status.ts";
import { fitText, measureText, wrapPx } from "../engine/font.ts";
import { easeOut } from "../engine/gfx.ts";
import type { Ctx } from "../game/ctx.ts";
import { focus, glass, hint, typeBadge } from "../ui/kit.ts";
import { worldHour } from "../world/fx.ts";
import { actorFrame, peerOutfit, TRAINER_OUTFITS } from "../world/actors.ts";
import type { DuelChoice, DuelEvent, DuelInfo, DuelNext, ServerMsg, Snap } from "../net/protocol.ts";
import { hash2 } from "../world/noise.ts";
import {
  actsFirst,
  applyWinExp,
  beforeMove,
  bountyFor,
  endOfTurn,
  catchChance,
  clearLoweredStats,
  effStat,
  guardBreakHint,
  statDropHint,
  attackPpHint,
  pickWildMove,
  throwCapsule,
  tickShown,
  useMove,
  wrapBattle,
  type BattleMon,
} from "../game/combat.ts";
import { isFaint, maxHp, mintMon, moveDef, replaceMove, usableMoves, type Evolution, type Mon } from "../game/mon.ts";
import { bagAdd, bagCount, clinicHeal, commitRng, living, markCaught, markSeen, persist, stashMon } from "../game/save.ts";
import type { Scene, ScenePayload } from "../game/scenes.ts";
import { wrapIndex } from "../ui/menu.ts";

type Phase =
  | "intro"
  | "text"
  | "command"
  | "tactics"
  | "concede"
  | "moves"
  | "pack"
  | "crew"
  | "catch"
  | "attack"
  | "learn"
  | "forget"
  | "evolve"
  | "netwait";

interface LearnOffer {
  mon: Mon;
  moveId: string;
}

type Next = Phase | (() => void);

export const THROW_MS = 900;
export const SHAKE_MS = 620;
export const SETTLE_MS = 700;
const SLIDE_MS = 600;
const INTRO_MS = 900;
const LEAVE_MS = 360;
const TEXT_MS = 12;
const HIT_HOLD = 40;
export const EVO_MS = 3200;
const EVO_SWAP = 2200;
const EVO_FLASH_LO = 2000;
const EVO_FLASH_HI = 2500;

/** Fable chrome click-in clocks (kept on quality merge). */
const MENU_MS = 88;
const PHASE_MS: Partial<Record<Phase, number>> = { pack: 76, crew: 76, moves: 84, tactics: 108, concede: 96, learn: 96, forget: 96 };

export function battleScene(ctx: Ctx): Scene {
  const query = new URLSearchParams(typeof window !== "undefined" ? window.location?.search ?? "" : "");
  const timeOverride = query.get("time");
  const previewZone = query.get("bzone");
  let you: BattleMon;
  let foe: BattleMon;
  let trainer: TrainerDef | undefined;
  let partnerIndex = 0;
  let foeOpened = false;
  let foePreviousMove: MoveDef | undefined;
  let phase: Phase = "intro";
  let cmd = 0;
  let moveI = 0;
  let packI = 0;
  let crewI = 0;
  let intro = 0;
  let slide = 0;
  let menuAge = 0;
  let menuPhase: Phase | "" = "";
  let queue: string[] = [];
  let shown = "";
  let waiting = false;
  let afterText: Next = "command";
  let textT = 0;
  let textHold = 0;
  let advancePending = false;
  /** Auto-advance after the line is fully shown (ms); 0 waits for A. */
  let autoMs = 0;
  let catchT = 0;
  let shakes = 0;
  let catchOk = false;
  let catchItem: ItemId = "capsule";
  let captured = false;
  let evolutions: Evolution[] = [];
  let evolutionT = 0;
  let afterEvolutions: () => void = () => leaveBattle();
  let learnQueue: LearnOffer[] = [];
  let learnOffer: LearnOffer | null = null;
  let learnChoice = 0;
  let forgetI = 0;
  let previewFlash = 0;
  let previewAt = -1;
  const previewSeen = new Set<number>();
  let attack: (AttackCue & {
    user: BattleMon;
    target: BattleMon;
    lines: string[];
    done: () => void;
  }) | null = null;
  let leaving = false;
  let leaveT = 0;
  let leaveBlackout = false;

  /** Live duel against another player: the server rolls, this scene replays its events. */
  type TurnMsg = Extract<ServerMsg, { t: "duelTurn" }>;
  let pvp: {
    info: DuelInfo;
    team: Mon[];
    foeTeam: Mon[];
    foeIndex: number;
    turn: number;
    inbox: TurnMsg[];
    foeReady: boolean;
    deadline: number;
    over: boolean;
    unsub: () => void;
  } | null = null;
  let pvpMove: Extract<DuelEvent, { kind: "move" }> | null = null;
  /** Raid: the shared pool at the start of the turn being replayed. */
  let raidTurnHp = 0;

  /** Raid boss health on the plate is the shared pool, scaled onto the boss's own bar. */
  function showPool(hp: number): void {
    const raid = pvp?.info.raid;
    if (!raid || !foe) return;
    const max = maxHp(foe.mon);
    foe.mon.hp = hp <= 0 ? 0 : Math.max(1, Math.ceil((max * Math.min(raid.max, hp)) / raid.max));
  }

  /** Whoever the CREW menu lists: the save's party, or this duel's exhibition copies. */
  function crew(): Mon[] {
    return pvp ? pvp.team : ctx.state.party;
  }

  function sendChoice(choice: DuelChoice): void {
    if (!pvp) return;
    ctx.net.send({ t: "duelChoice", id: pvp.info.id, turn: pvp.turn, choice });
    phase = "netwait";
  }

  function applySnap(b: BattleMon, snap: Snap): void {
    b.mon.hp = snap.hp;
    snap.pp.forEach((pp, i) => {
      if (b.mon.moves[i]) b.mon.moves[i]!.pp = pp;
    });
    b.atk = snap.atk;
    b.def = snap.def;
    b.spd = snap.spd;
    b.sureHit = snap.sureHit;
    b.flinched = snap.flinched;
    b.status = snap.status ?? null;
    b.statusTurns = snap.statusTurns ?? 0;
    if (pvp?.info.raid && b === foe) showPool(raidTurnHp - (maxHp(b.mon) - snap.hp));
  }

  /** Apply both post-event snapshots from a server event. */
  function applyBoth(e: { a: Snap; b: Snap }): void {
    if (!pvp) return;
    const side = pvp.info.side;
    applySnap(you, e[side]);
    applySnap(foe, e[side === "a" ? "b" : "a"]);
    tickShown(you, ctx.reduced, 0);
    tickShown(foe, ctx.reduced, 0);
  }

  function playTurn(msg: TurnMsg): void {
    if (!pvp) return;
    const duel = pvp;
    if (duel.info.raid) raidTurnHp = duel.info.raid.hp;
    duel.turn = msg.turn + 1;
    duel.foeReady = false;
    const side = duel.info.side;
    const foeName = duel.info.foe.name;
    const events = [...msg.events];
    const next = (): void => {
      const e = events.shift();
      if (!e) {
        afterTurn(msg.next);
        return;
      }
      const mine = e.side === side;
      if (e.kind === "switch") {
        if (mine) {
          you = wrapBattle(duel.team[e.index]!);
          moveI = 0;
          say([`Go, ${you.mon.nick}!`], next, 0, 520);
        } else {
          duel.foeIndex = e.index;
          foe = wrapBattle(duel.foeTeam[e.index]!);
          markSeen(ctx.state, foe.mon.speciesId);
          say([`${foeName} sends ${foe.mon.nick}!`], next, 0, 520);
        }
      } else if (e.kind === "move") {
        const user = mine ? you : foe, target = mine ? foe : you;
        const mv = moveById(e.moveId);
        say([`${user.mon.nick} uses ${mv.name}!`], () => {
          pvpMove = e;
          attack = { user, target, move: mv, own: mine, elapsed: 0, result: null, lines: [], done: next };
          phase = "attack";
          if (ctx.reduced) {
            resolveAttack();
            finishAttack();
          }
        }, 0, 520);
      } else if (e.kind === "flinch") {
        say([`${(mine ? you : foe).mon.nick} flinches! Lost the turn.`], next);
      } else if (e.kind === "status") {
        applyBoth(e);
        if (e.skip && !ctx.reduced) (mine ? you : foe).shake = 90;
        say(e.notes, next);
      } else if (e.kind === "tick") {
        const b = mine ? you : foe;
        applyBoth(e);
        if (!ctx.reduced) {
          b.flash = 50;
          b.shake = 80;
        }
        ctx.audio.hit();
        say([e.note], next, HIT_HOLD);
      } else if (e.kind === "timeout") {
        say([mine ? "Your turn clock ran out." : `${foeName} ran out of time.`], next);
      } else if (e.kind === "forfeit") {
        say([mine ? "You conceded the duel." : `${foeName} conceded.`], next);
      }
    };
    next();
  }

  function afterTurn(nx: DuelNext): void {
    if (!pvp) return;
    const duel = pvp;
    const side = duel.info.side;
    you.flinched = foe.flinched = false;
    if (duel.info.raid) {
      showPool(duel.info.raid.hp);
      tickShown(foe, ctx.reduced, 0);
      if (nx.kind === "end") {
        duel.over = true;
        const won = nx.winner === side;
        if (won) {
          ctx.goal("raidWin");
          ctx.audio.jingle("victory", null);
          ctx.audio.catchOk();
        } else ctx.audio.fail();
        say(won
          ? [`${foe.mon.nick} falls! The raid is won.`, nx.prize ? `+${nx.prize} coins and Myth Tins for every raider.` : "Rewards go to everyone who landed a hit."]
          : [`${you.mon.nick} and the crew are out.`, "Every hit you landed still counts toward the pool."], () => leaveBattle());
        return;
      }
    }
    if (nx.kind === "choose") {
      duel.deadline = ctx.now + duel.info.turnMs;
      phase = "command";
      return;
    }
    if (nx.kind === "replace") {
      if (nx.sides.includes(side)) {
        crewI = Math.max(0, duel.team.findIndex((m) => !isFaint(m)));
        duel.deadline = ctx.now + duel.info.turnMs;
        say([`${you.mon.nick} is out.`, "Send the next meme."], "crew");
      } else {
        say([`${foe.mon.nick} is out.`], "netwait");
      }
      return;
    }
    duel.over = true;
    const won = nx.winner === side;
    if (won) ctx.goal("duelWin");
    const before = duel.info.you.rating, after = nx.rating[side];
    const delta = after - before;
    if (won) {
      ctx.audio.jingle("victory", null);
      ctx.audio.catchOk();
    } else ctx.audio.fail();
    say([
      won ? `You beat ${duel.info.foe.name}!` : `${duel.info.foe.name} wins the duel.`,
      won && nx.prize ? `+${nx.prize} coins duel prize.` : "",
      `Rating ${after} (${delta >= 0 ? "+" : ""}${delta}).`,
    ], () => leaveBattle());
  }

  function resolveAttack(): void {
    if (!attack || attack.result) return;
    const { user, target, move } = attack;
    const hadStatus = target.status;
    // One resolution at contact. The visual clock never rolls dice or copies combat state.
    let r: ReturnType<typeof useMove>;
    if (pvp && pvpMove) {
      // Live duel: the server already rolled; apply its snapshots.
      const side = pvp.info.side;
      applySnap(you, pvpMove[side]);
      applySnap(foe, pvpMove[side === "a" ? "b" : "a"]);
      r = pvpMove.result;
      pvpMove = null;
      if (r.hit && r.dmg > 0) {
        target.flash = 70;
        target.shake = r.crit ? 160 : 110;
      }
    } else r = useMove(ctx.rng, user, target, move);
    if (user === foe) {
      foeOpened = true;
      foePreviousMove = move;
    }
    attack.result = r;
    commitRng(ctx.state, ctx.rng);
    if (user === you && r.hit) {
      if (r.dmg > 0 && r.mod > 1) ctx.goal("superHit");
      if (!hadStatus && target.status) ctx.goal("condition");
      if (pvp?.info.raid && r.dmg > 0) ctx.goal("raidDamage", r.dmg);
    }
    if (!r.hit) ctx.audio.back();
    else if (r.crit) ctx.audio.crit();
    else if (r.dmg) ctx.audio.hit();
    else if (move.fx?.kind === "heal" || move.fx?.kind === "cure") ctx.audio.heal();
    else ctx.audio.confirm();
    if (r.hit && !ctx.reduced) ctx.audio.flourish(attackStyle(move));
    attack.lines = r.dmg ? [`${r.crit ? "CRITICAL HIT! " : ""}${target.mon.nick} takes ${r.dmg} HP.`] : [];
    attack.lines.push(...r.notes.filter((note) => note !== "CRITICAL HIT!"));
    tickShown(user, ctx.reduced, 0);
    tickShown(target, ctx.reduced, 0);
  }

  function finishAttack(): void {
    if (!attack) return;
    const { lines, done } = attack;
    attack = null;
    say(lines, done, HIT_HOLD);
    shown = lines[0] ?? "";
    waiting = true;
  }

  function say(lines: string[], next: Next, hold = 0, auto = 0): void {
    autoMs = ctx.reduced ? 0 : auto;
    queue = lines.filter(Boolean);
    shown = ctx.reduced ? queue[0] ?? "" : "";
    waiting = ctx.reduced;
    textT = 0;
    textHold = hold;
    advancePending = false;
    afterText = next;
    phase = "text";
  }

  function finishText(): void {
    if (typeof afterText === "function") afterText();
    else phase = afterText;
  }

  function typeTick(): void {
    if (queue.length === 0) {
      finishText();
      return;
    }
    const full = queue[0]!;
    textT += ctx.dt;
    shown = full.slice(0, ctx.reduced || waiting ? full.length : Math.floor(textT / TEXT_MS));
    waiting = shown.length === full.length;
  }

  function settled(): boolean {
    return [you, foe].every((b) => b.shownHp === b.mon.hp && (!isFaint(b.mon) || b.faint === 1));
  }

  function advanceText(): void {
    if (!waiting) {
      shown = queue[0] ?? "";
      waiting = true;
      return;
    }
    if (textT < textHold || (textHold > 0 && !settled())) {
      advancePending = true;
      return;
    }
    advancePending = false;
    queue.shift();
    shown = ctx.reduced ? queue[0] ?? "" : "";
    waiting = ctx.reduced;
    textT = 0;
    textHold = 0;
    if (queue.length === 0) finishText();
  }

  function packItems(): { id: ItemId; n: number }[] {
    const ids: ItemId[] = ["sip", "tallsip", "fullcope", "capsule", "gloss", "myth", "cleartea"];
    return ids.map((id) => ({ id, n: bagCount(ctx.state, id) })).filter((x) => x.n > 0);
  }

  function sendLead(): void {
    const mon = living(ctx.state)[0] ?? ctx.state.party[0]!;
    you = wrapBattle(mon);
  }

  function act(user: BattleMon, target: BattleMon, mv: MoveDef, done: () => void): void {
    if (isFaint(user.mon) || isFaint(target.mon)) {
      done();
      return;
    }
    if (user.flinched) {
      user.flinched = false;
      say([`${user.mon.nick} flinches! Lost the turn.`], done);
      return;
    }
    const check = beforeMove(ctx.rng, user);
    commitRng(ctx.state, ctx.rng);
    if (check.skip) {
      if (!ctx.reduced) user.shake = 90;
      say(check.notes, done);
      return;
    }
    say([...check.notes, `${user.mon.nick} uses ${mv.name}!`], () => {
      attack = { user, target, move: mv, own: user === you, elapsed: 0, result: null, lines: [], done };
      phase = "attack";
      if (ctx.reduced) {
        resolveAttack();
        finishAttack();
      }
    }, 0, 520);
  }

  function wildAct(): void {
    act(foe, you, opponentMove(), endTurn);
  }

  /** Scorch and FUD bite once both sides have acted, before faints are tallied. */
  function endTurn(): void {
    const lines: string[] = [];
    for (const b of [you, foe]) {
      const tick = endOfTurn(b);
      if (!tick) continue;
      lines.push(tick.note);
      if (!ctx.reduced) {
        b.flash = 50;
        b.shake = 80;
      }
      tickShown(b, ctx.reduced, 0);
    }
    if (lines.length) {
      ctx.audio.hit();
      say(lines, afterActions, HIT_HOLD);
    } else afterActions();
  }

  function opponentMove(): MoveDef {
    const opening = trainer?.team[partnerIndex]?.openingMove;
    if (!foeOpened && opening && foe.mon.moves.some((m) => m.id === opening && m.pp > 0)) return moveById(opening);
    return pickWildMove(ctx.rng, foe, you, foePreviousMove);
  }

  function sendNextPartner(): void {
    const partner = trainer!.team[++partnerIndex]!;
    foe = wrapBattle(mintMon(ctx.rng, partner.species, partner.level, trainer!.encore !== undefined ? "level" : "designed"));
    markSeen(ctx.state, partner.species);
    commitRng(ctx.state, ctx.rng);
    foeOpened = false;
    foePreviousMove = undefined;
    cmd = moveI = 0;
    previewSeen.clear();
    const forced = isFaint(you.mon);
    if (forced) crewI = ctx.state.party.findIndex((m) => !isFaint(m));
    say([`${trainer!.name} sends ${foe.mon.nick}!`, partner.arrival ?? "",
      forced ? "Send the next meme." : ""], forced ? "crew" : "command");
  }

  function actuallyLeave(blackout: boolean): void {
    if (blackout) {
      ctx.state.x = WAKE.x;
      ctx.state.y = WAKE.y;
      clinicHeal(ctx.state);
    }
    commitRng(ctx.state, ctx.rng);
    persist(ctx.state);
    ctx.pop();
  }

  function leaveBattle(blackout = false): void {
    if (leaving) return;
    if (ctx.reduced) {
      actuallyLeave(blackout);
      return;
    }
    leaving = true;
    leaveT = LEAVE_MS;
    leaveBlackout = blackout;
  }

  function lossPenalty(): number {
    return Math.min(ctx.state.money, Math.max(20, Math.floor(ctx.state.money * 0.1)));
  }

  function learnedNames(ids: string[]): string {
    return ids.map((id) => moveById(id).name).join(" / ");
  }

  function queueLearns(mon: Mon, pending: string[]): void {
    for (const moveId of pending) learnQueue.push({ mon, moveId });
  }

  function nextLearn(): void {
    if (!learnQueue.length) {
      learnOffer = null;
      nextEvolution();
      return;
    }
    learnOffer = learnQueue.shift()!;
    if (learnOffer.mon.moves.some((s) => s.id === learnOffer!.moveId)) {
      nextLearn();
      return;
    }
    learnChoice = 0;
    forgetI = 0;
    phase = "learn";
    ctx.audio.heal();
  }

  function nextEvolution(): void {
    if (!evolutions.length) {
      afterEvolutions();
      return;
    }
    evolutionT = 0;
    phase = "evolve";
    ctx.audio.heal();
  }

  function finishVictory(blackout = false): void {
    afterEvolutions = () => leaveBattle(blackout);
    nextLearn();
  }

  function afterActions(): void {
    commitRng(ctx.state, ctx.rng);
    you.flinched = foe.flinched = false;
    const lines: string[] = [];
    if (isFaint(foe.mon)) {
      if (!trainer) ctx.goal("wildWin");
      const pay = bountyFor(foe.mon, false);
      ctx.state.money += pay;
      const xp = applyWinExp(you.mon, foe.mon, !!trainer);
      evolutions = xp.evolutions;
      learnQueue = [];
      queueLearns(you.mon, xp.pendingLearns);
      lines.push(`${foe.mon.nick} is out. +${pay} coins`);
      // Every crew member's exp in as few boxes as fit: "Shibonk +25 exp Lv12!, Bogfiend +12, ...".
      const gains = [`${you.mon.nick} +${xp.exp} exp${xp.leveled ? ` (Lv${you.mon.level}!)` : ""}`];
      const learned: string[] = xp.learned.length ? [`${you.mon.nick} learned ${learnedNames(xp.learned)}.`] : [];
      for (const reserve of living(ctx.state).filter((m) => m.uid !== you.mon.uid)) {
        const shared = applyWinExp(reserve, foe.mon, !!trainer, 0.5);
        evolutions.push(...shared.evolutions);
        queueLearns(reserve, shared.pendingLearns);
        gains.push(`${reserve.nick} +${shared.exp}${shared.leveled ? ` (Lv${reserve.level}!)` : ""}`);
        if (shared.learned.length) learned.push(`${reserve.nick} learned ${learnedNames(shared.learned)}.`);
      }
      lines.push(...packLines(gains), ...learned);
      for (const evolution of evolutions) markCaught(ctx.state, evolution.to);
      ctx.audio.catchOk();
      if (xp.leveled) ctx.audio.levelUp();
      if (!trainer || partnerIndex + 1 >= trainer.team.length) ctx.audio.jingle("victory", null);
    }
    if (isFaint(you.mon)) {
      you.mon.hp = 0;
      lines.push(`${you.mon.nick} is out.`);
      if (living(ctx.state).length === 0) {
        const loss = lossPenalty();
        ctx.state.money -= loss;
        say([...lines, `The crew is cooked. -${loss} coins`, trainer?.lossAdvice ?? "",
          "Waking up at Cope Clinic. Kettle's already mad."], () => finishVictory(true));
        ctx.audio.fail();
        return;
      }
      if (!isFaint(foe.mon)) {
        crewI = ctx.state.party.findIndex((m) => !isFaint(m));
        say([...lines, "Send the next meme."], "crew");
        return;
      }
    }
    if (isFaint(foe.mon)) {
      if (trainer && partnerIndex + 1 < trainer.team.length) {
        say(lines, () => {
          afterEvolutions = sendNextPartner;
          nextLearn();
        });
        return;
      }
      if (trainer?.encore !== undefined) {
        // Post-game encore: one prize a week per trainer; the lock opens again on Monday.
        const prize = encorePrize(trainer.id);
        ctx.state.encore[trainer.id] = trainer.encore;
        ctx.state.money += prize;
        ctx.goal("trainerWin");
        const left = Object.keys(TRAINERS).filter((id) => !isCircuitTrainer(id as TrainerDef["id"])
          && ctx.state.encore[id as TrainerDef["id"]] !== trainer!.encore).length;
        lines.push(`${trainer.name}: ${trainer.defeat}`, `Encore won! +${prize} coins prize. ${
          left ? `${left} encore${left === 1 ? "" : "s"} left this week.` : "Every encore this week is won. New crews arrive on Monday."}`);
      } else if (trainer) {
        const rematch = ctx.state.defeatedTrainers.includes(trainer.id);
        const prize = rematch ? trainer.rematchPrize ?? 0 : trainer.prize;
        const practicing = rematch && !CIRCUIT_IDS.every(id => ctx.state.defeatedTrainers.includes(id));
        if (rematch && !practicing) ctx.state.circuitRematches += 1;
        if (practicing && isCircuitTrainer(trainer.id)) ctx.state.circuitPracticeWins[trainer.id] += 1;
        if (!rematch) ctx.state.defeatedTrainers.push(trainer.id);
        ctx.state.money += prize;
        ctx.goal("trainerWin");
        lines.push(`${trainer.name}: ${trainer.defeat}`);
        // The prize, supplies and what comes next share as few boxes as fit.
        const after: string[] = [`Duel won! +${prize} coins prize.`];
        if (!rematch && trainer.supplies) {
          bagAdd(ctx.state, trainer.supplies.id, trainer.supplies.count);
          after.push(`Packed ${trainer.supplies.count} ${ITEMS[trainer.supplies.id].name}.`);
        }
        if (!rematch && trainer.id === "ilex") {
          after.push("Recipe delivered! Clear Tea now sells at Signal Stall.");
        }
        if (trainer.rematchPrize && (rematch || trainer.id !== "nix")) {
          const next = CIRCUIT_IDS.find(id => !ctx.state.defeatedTrainers.includes(id))
            ?? CIRCUIT_IDS[(CIRCUIT_IDS.findIndex(id => id === trainer!.id) + 1) % CIRCUIT_IDS.length]!;
          after.push(practicing ? "Practice won! No extra stamp or supplies." : "Circuit stamp earned.",
            `Next: ${TRAINERS[next].name}. Rest at a kettle first.`);
        } else if (!rematch) {
          // A first win always names the next stop on PLAN, so no region hand-off leaves the scout guessing.
          if (trainer.id === "nix") after.push("Circuit cleared! Laps stay open for practice.");
          const nextId = nextDuel({ ...ctx.state, week: weekOf(ctx.today()) });
          const next = TRAINERS[nextId];
          after.push(ctx.state.defeatedTrainers.includes(nextId)
            ? "Every duel is won. Each week, the trainers come back for encores."
            : `Next on PLAN: ${next.name}, ${ZONES[zoneAt(next.x, next.y)].name}.`);
        }
        lines.push(...packLines(after, 3, " "));
      }
      say(lines, () => finishVictory());
      return;
    }
    phase = "command";
  }

  function fight(moveIndex: number): void {
    const slots = usableMoves(you.mon);
    const slot = slots.length ? slots[Math.min(moveIndex, slots.length - 1)]! : { id: "flailPost", pp: 1 };
    const mv = moveDef(slot);
    const wildMove = opponentMove();
    const youFirst = actsFirst(you, mv, foe, wildMove) ?? ctx.rng.chance(0.5);
    you.flinched = foe.flinched = false;
    if (youFirst) {
      act(you, foe, mv, () => act(foe, you, wildMove, endTurn));
    } else {
      act(foe, you, wildMove, () => act(you, foe, mv, endTurn));
    }
  }

  function runAway(): void {
    if (trainer || pvp) {
      phase = "concede";
      return;
    }
    const youSpd = effStat(you, "spd");
    const foeSpd = effStat(foe, "spd");
    const ok = ctx.rng.chance(Math.max(0.1, Math.min(0.95, 0.5 + (youSpd - foeSpd) * 0.05)));
    commitRng(ctx.state, ctx.rng);
    if (ok) {
      say(["Got out of the thread."], () => leaveBattle());
    } else {
      say(["They cut you off."], wildAct);
    }
  }

  function usePack(id: ItemId): void {
    if (bagCount(ctx.state, id) <= 0) return;
    const def = ITEMS[id];
    if (def.kind === "cleanse") {
      const cured = you.status ? STATUS_META[you.status].name : null;
      if (!clearLoweredStats(you) && !cured) {
        say(["No lowered stats. Save the tea."], "pack");
        return;
      }
      you.status = null;
      you.statusTurns = 0;
      bagAdd(ctx.state, id, -1);
      ctx.audio.heal();
      say([`${you.mon.nick} drinks ${def.name}. ${cured ? `No longer ${cured}.` : "Lowered stats cleared."} Boosts kept.`],
        wildAct, ctx.reduced ? 80 : 120);
      return;
    }
    if (def.kind === "heal") {
      if (you.mon.hp >= maxHp(you.mon)) {
        say(["Already full."], "pack");
        return;
      }
      bagAdd(ctx.state, id, -1);
      you.mon.hp = Math.min(maxHp(you.mon), you.mon.hp + itemHeal(def, maxHp(you.mon)));
      ctx.audio.heal();
      tickShown(you, ctx.reduced, 0);
      say([`${you.mon.nick} drinks ${def.name}.`], wildAct, ctx.reduced ? 80 : 120);
      return;
    }
    if (def.kind === "capsule") {
      if (trainer) {
        say(["A trainer's partner cannot be caught."], "pack");
        return;
      }
      bagAdd(ctx.state, id, -1);
      const roll = throwCapsule(ctx.rng, foe.mon, id, foe.status);
      commitRng(ctx.state, ctx.rng);
      catchItem = id;
      shakes = roll.shakes;
      catchOk = roll.caught;
      catchT = 0;
      phase = "catch";
      ctx.audio.toss();
    }
  }

  function finishCatch(): void {
    if (catchOk) {
      captured = true;
      markCaught(ctx.state, foe.mon.speciesId);
      ctx.goal("catch", 1, { species: foe.mon.speciesId, shiny: !!foe.mon.shiny });
      const where = stashMon(ctx.state, foe.mon);
      const pay = bountyFor(foe.mon, true);
      ctx.state.money += pay;
      persist(ctx.state);
      ctx.audio.catchOk();
      ctx.audio.jingle("victory", null);
      say(
        [where === "pc"
          ? `Gotcha! ${foe.mon.nick} is yours. Party full: stashed in the Meme Box. +${pay} coins`
          : `Gotcha! ${foe.mon.nick} joined the crew. +${pay} coins`],
        () => leaveBattle(),
      );
    } else {
      ctx.audio.fail();
      say(["It popped out!"], wildAct);
    }
  }

  function armPreview(index: number): void {
    moveI = index;
    if (previewSeen.has(index)) {
      previewFlash = 0;
      previewAt = -1;
      return;
    }
    previewSeen.add(index);
    previewAt = index;
        previewFlash = ctx.reduced ? 0 : 180;
  }

  return {
    id: "battle",
    native: true,
    debug: () => ({ phase, cmd, moveI, packI, crewI, intro, catchT, evolutionT, learnChoice, forgetI,
      you, foe, trainer: trainer?.id, partnerIndex, learnOffer }),
    enter(payload?: ScenePayload) {
      pvp?.unsub();
      pvp = null;
      pvpMove = null;
      const duel = payload?.duel;
      if (duel) {
        pvp = {
          info: duel, team: duel.you.team, foeTeam: duel.foe.team, foeIndex: 0, turn: 0, inbox: [], foeReady: false,
          deadline: ctx.now + duel.turnMs, over: false,
          unsub: ctx.net.on((m) => {
            if (!pvp || pvp.info.id !== duel.id) return;
            if (m.t === "duelTurn" && m.id === duel.id) pvp.inbox.push(m);
            else if (m.t === "duelWait" && m.id === duel.id) pvp.foeReady = m.foeReady;
            else if (m.t === "raidPool" && m.id === duel.id && !attack && ["command", "moves", "netwait", "tactics", "crew"].includes(phase)) {
              // Other scouts' hits land between our turns.
              showPool(m.pool.hp);
            }
          }),
        };
      }
      trainer = !duel && payload?.trainer ? TRAINERS[payload.trainer] : undefined;
      const withWeek = { ...ctx.state, week: weekOf(ctx.today()) };
      if (trainer && trainerLock(trainer, withWeek)) {
        ctx.pop();
        return;
      }
      if (trainer) trainer = trainerEncounter(trainer, withWeek);
      partnerIndex = 0;
      foeOpened = false;
      foePreviousMove = undefined;
      const first = trainer?.team[0];
      const wild = pvp ? pvp.foeTeam[0] : first ? mintMon(ctx.rng, first.species, first.level, trainer?.encore !== undefined ? "level" : "designed") : payload?.wild;
      if (!wild) {
        ctx.pop();
        return;
      }
      if (pvp) you = wrapBattle(pvp.team[0]!);
      else sendLead();
      ctx.audio.music("battle");
      foe = wrapBattle(wild);
      markSeen(ctx.state, wild.speciesId);
      commitRng(ctx.state, ctx.rng);
      phase = "intro";
      intro = 0;
      slide = ctx.reduced ? 1 : 0;
      cmd = 0;
      moveI = 0;
      captured = false;
      evolutions = [];
      learnQueue = [];
      learnOffer = null;
      evolutionT = 0;
      attack = null;
      leaving = false;
      leaveT = 0;
      leaveBlackout = false;
      previewFlash = 0;
      previewAt = -1;
      previewSeen.clear();
      queue = [];
      if (ctx.shot === "catch") {
        slide = 1;
        phase = "catch";
        catchItem = "gloss";
        shakes = 3;
        catchOk = true;
        catchT = THROW_MS + SHAKE_MS;
      }
      if (!trainer && ["battle", "evolve", "batch-a", "evolve-a", "batch-b", "evolve-b", "kits", "craft-c", "craft-d", "craft-e", "craft-f", "frontier-battle", "frontier-crystal", "frontier-bog"].includes(ctx.shot ?? "")) {
        slide = 1;
        phase = "command";
      }
      if (ctx.shot === "moves") {
        slide = 1;
        phase = "moves";
        previewSeen.add(0);
        previewAt = 0;
        previewFlash = 2000;
      }
    },
    exit() {
      if (pvp) {
        pvp.unsub();
        if (ctx.net.duel?.id === pvp.info.id) ctx.net.duel = null;
      }
      ctx.grassQuiet = GRASS_QUIET;
      ctx.g.shakeX = ctx.g.shakeY = 0;
    },
    update() {
      if (!you || !foe) return;
      if (phase !== menuPhase) {
        menuPhase = phase;
        menuAge = enterAge(ctx.shot);
      } else {
        menuAge += ctx.dt;
      }
      tickShown(you, ctx.reduced, ctx.dt);
      tickShown(foe, ctx.reduced, ctx.dt);

      if (leaving) {
        leaveT -= ctx.dt;
        if (ctx.input.consumeAny("Enter", "Escape")) leaveT = 0;
        if (leaveT <= 0) actuallyLeave(leaveBlackout);
        return;
      }

      if (pvp && !pvp.over) {
        if (!ctx.net.live) {
          pvp.over = true;
          say(["Connection lost. The duel is void."], () => leaveBattle());
          return;
        }
        if (pvp.inbox.length && ["command", "moves", "pack", "crew", "tactics", "concede", "netwait"].includes(phase)) {
          playTurn(pvp.inbox.shift()!);
          return;
        }
      }
      if (phase === "netwait") {
        ctx.input.consumeAny("Enter", "Escape");
        return;
      }

      if (phase === "learn" && learnOffer) {
        const d = ctx.input.menuDir(ctx.now);
        if (d && d.x) {
          learnChoice = wrapIndex(learnChoice, 2, d.x, 0, 2);
          ctx.audio.cursor();
        }
        if (ctx.input.consume("Escape")) {
          ctx.audio.back();
          const skipped = learnOffer;
          learnOffer = null;
          say([`${skipped.mon.nick} skipped ${moveById(skipped.moveId).name}.`], nextLearn);
          return;
        }
        if (ctx.input.consume("Enter")) {
          ctx.audio.confirm();
          if (learnChoice === 1) {
            const skipped = learnOffer;
            learnOffer = null;
            say([`${skipped.mon.nick} skipped ${moveById(skipped.moveId).name}.`], nextLearn);
          } else {
            forgetI = 0;
            phase = "forget";
          }
        }
        return;
      }

      if (phase === "forget" && learnOffer) {
        const n = learnOffer.mon.moves.length;
        const d = ctx.input.menuDir(ctx.now);
        if (d) {
          forgetI = wrapIndex(forgetI, n, 0, d.y, 1);
          ctx.audio.cursor();
        }
        if (ctx.input.consume("Escape")) {
          ctx.audio.back();
          learnChoice = 0;
          phase = "learn";
          return;
        }
        if (ctx.input.consume("Enter")) {
          ctx.audio.confirm();
          const offer = learnOffer;
          const forgotten = offer.mon.moves[forgetI]!;
          replaceMove(offer.mon, forgetI, offer.moveId);
          learnOffer = null;
          say([`${offer.mon.nick} forgot ${moveById(forgotten.id).name} and learned ${moveById(offer.moveId).name}!`], nextLearn);
        }
        return;
      }

      if (phase === "evolve") {
        evolutionT += ctx.dt;
        if (ctx.input.consumeAny("Enter", "Escape")) evolutionT = EVO_MS;
        if (ctx.reduced || evolutionT >= EVO_MS) {
          const evolution = evolutions.shift()!;
          const evolved = speciesById(evolution.to);
          const upgrades = (evolution.upgradedMoves ?? []).map((id) => moveById(id).name);
          say([`${speciesById(evolution.from).name} evolved into ${evolved.name}!${upgrades.length ? ` Kit upgraded: ${upgrades.join(" / ")}.` : ""}`], nextEvolution);
        }
        return;
      }

      if (phase === "attack" && attack) {
        const timing = attackTiming(attack.move);
        attack.elapsed = ctx.reduced ? timing.duration : Math.min(timing.duration, attack.elapsed + ctx.dt);
        if (attack.elapsed >= timing.impact) resolveAttack();
        if (attack.elapsed >= timing.duration) finishAttack();
        // Confirm/back cannot skip contact, replay damage or spend a second PP.
        ctx.input.consumeAny("Enter", "Escape");
        return;
      }

      if (phase === "intro") {
        intro += ctx.dt;
        const skipped = ctx.input.consumeAny("Enter", "Escape");
        if (skipped) {
          intro = INTRO_MS;
          ctx.audio.confirm();
        }
        slide = ctx.reduced ? 1 : Math.min(1, intro / SLIDE_MS);
        if (ctx.reduced || intro >= INTRO_MS) {
          say(trainer
            ? [`${trainer.name}: ${trainer.intro}`, `${trainer.name} sends ${foe.mon.nick}!`,
              trainer.team[0].arrival ?? "", `Go, ${you.mon.nick}!`]
            : pvp?.info.raid
              ? [`RAID BOSS ${foe.mon.nick} blocks the gate!`, "Every scout's hits drain the same pool.", `Go, ${you.mon.nick}!`]
            : pvp
              ? [`${pvp.info.foe.title ? `${pvp.info.foe.title} ` : ""}${pvp.info.foe.name} wants to duel!`, `${pvp.info.foe.name} sends ${foe.mon.nick}!`, `Go, ${you.mon.nick}!`]
              : foe.mon.shiny
                ? [`A SHINY ${foe.mon.nick} rustles out! ✦`, "Its colors are one in 256.", `Go, ${you.mon.nick}!`]
                : [`A wild ${foe.mon.nick} rustles out!`, `Go, ${you.mon.nick}!`], "command");
          if (foe.mon.shiny && !pvp && !trainer) ctx.audio.levelUp();
          if (pvp) pvp.deadline = ctx.now + pvp.info.turnMs;
          if (skipped) {
            shown = queue[0] ?? "";
            waiting = true;
          }
        }
        return;
      }

      if (phase === "text") {
        typeTick();
        if (autoMs > 0 && waiting && queue.length === 1 && textT >= (queue[0]!.length * TEXT_MS) + autoMs) {
          autoMs = 0;
          advanceText();
          return;
        }
        if (ctx.input.consumeAny("Enter", "Escape")) {
          ctx.audio.confirm();
          advanceText();
        } else if (advancePending) advanceText();
        return;
      }

      if (phase === "catch") {
        if (ctx.shot === "catch") {
          if (!ctx.input.consumeAny("Enter", "Escape")) return;
          ctx.shot = null;
        }
        const before = catchT;
        catchT += ctx.dt;
        if (ctx.reduced) {
          finishCatch();
          return;
        }
        for (let n = 0; n < shakes; n++) {
          const at = THROW_MS + n * SHAKE_MS;
          if (before < at && catchT >= at) ctx.audio.catchShake();
        }
        if (catchT >= THROW_MS + shakes * SHAKE_MS + SETTLE_MS) finishCatch();
        return;
      }

      if (phase === "concede") {
        if (ctx.input.consume("Escape")) {
          ctx.audio.back();
          phase = "command";
        } else if (pvp && ctx.input.consume("Enter")) {
          ctx.audio.fail();
          sendChoice({ kind: "forfeit" });
        } else if (ctx.input.consume("Enter")) {
          const loss = lossPenalty();
          ctx.state.money -= loss;
          ctx.audio.fail();
          say([`Duel conceded. -${loss} coins`, trainer?.lossAdvice ?? "",
            "Returning to Cope Clinic."], () => leaveBattle(true));
        }
        return;
      }

      if (phase === "tactics") {
        if (ctx.input.consumeAny("Enter", "Escape")) {
          ctx.audio.back();
          phase = "command";
        }
        return;
      }

      if (phase === "command") {
        const d = ctx.input.menuDir(ctx.now);
        if (d) {
          cmd = wrapIndex(cmd, 4, d.x, d.y, 2);
          ctx.audio.cursor();
        }
        if (ctx.input.consume("Escape")) {
          ctx.audio.confirm();
          phase = "tactics";
          return;
        }
        if (ctx.input.consume("Enter")) {
          ctx.audio.confirm();
          if (cmd === 0) {
            const n = usableMoves(you.mon).length;
            moveI = Math.min(moveI, Math.max(0, n - 1));
            previewSeen.clear();
            armPreview(moveI);
            phase = "moves";
          } else if (cmd === 1 && pvp) {
            say(["No items in live duels. Pure skill."], "command");
          } else if (cmd === 1) {
            packI = 0;
            phase = "pack";
          } else if (cmd === 2) {
            crewI = 0;
            phase = "crew";
          } else runAway();
        }
        return;
      }

      if (phase === "moves") {
        if (previewFlash > 0) previewFlash = Math.max(0, previewFlash - ctx.dt);
        const slots = usableMoves(you.mon);
        const n = Math.max(1, slots.length);
        const d = ctx.input.menuDir(ctx.now);
        if (d) {
          const next = wrapIndex(moveI, n, d.x, d.y, 2);
          ctx.audio.cursor();
          armPreview(next);
        }
        if (ctx.input.consume("Escape")) {
          ctx.audio.back();
          phase = "command";
          return;
        }
        if (ctx.input.consume("Enter")) {
          ctx.audio.confirm();
          if (pvp) {
            const slot = slots[moveI];
            sendChoice({ kind: "move", slot: Math.max(0, slot ? you.mon.moves.indexOf(slot) : 0) });
          } else fight(moveI);
        }
        return;
      }

      if (phase === "pack") {
        const items = packItems();
        packI = Math.min(packI, Math.max(0, items.length - 1));
        if (items.length === 0) {
          if (ctx.input.consumeAny("Enter", "Escape")) {
            ctx.audio.back();
            phase = "command";
          }
          return;
        }
        const d = ctx.input.menuDir(ctx.now);
        if (d) {
          packI = wrapIndex(packI, items.length, 0, d.y, 1);
          ctx.audio.cursor();
        }
        if (ctx.input.consume("Escape")) {
          ctx.audio.back();
          phase = "command";
          return;
        }
        if (ctx.input.consume("Enter")) {
          ctx.audio.confirm();
          usePack(items[packI]!.id);
        }
        return;
      }

      if (phase === "crew") {
        const d = ctx.input.menuDir(ctx.now);
        if (d) {
          crewI = wrapIndex(crewI, crew().length, 0, d.y, 1);
          ctx.audio.cursor();
        }
        if (ctx.input.consume("Escape")) {
          if (isFaint(you.mon)) {
            say(["Pick a living meme to keep fighting."], "crew");
          } else {
            ctx.audio.back();
            phase = "command";
          }
          return;
        }
        if (ctx.input.consume("Enter")) {
          const pick = crew()[crewI]!;
          if (isFaint(pick)) {
            ctx.audio.fail();
            say([`${pick.nick} is out. Pick a living meme.`], "crew");
            return;
          }
          if (pick.uid === you.mon.uid) {
            phase = "command";
            return;
          }
          ctx.audio.confirm();
          if (pvp) {
            sendChoice({ kind: "switch", index: crewI });
            return;
          }
          const forced = isFaint(you.mon);
          you = wrapBattle(pick);
          moveI = 0;
          say([`Go, ${pick.nick}!`], forced ? "command" : wildAct, 32);
        }
      }
    },
    draw() {
      const g = ctx.g;
      const reduced = ctx.reduced;
      const now = ctx.now;
      const menuT = settleT(menuAge, reduced, PHASE_MS[phase] ?? MENU_MS);
      const zone = (previewZone as ZoneId | null) ?? zoneAt(ctx.state.x, ctx.state.y);
      const hour = worldHour(ctx.state, timeOverride);
      const tod = todFor(hour);
      g.shakeX = g.shakeY = 0;

      if (phase === "evolve" && evolutions[0]) {
        drawEvolution(ctx, evolutions[0], evolutionT);
        return;
      }

      drawStage(g, zone, tod, 1);
      const introT = phase === "intro" ? Math.min(1, intro / INTRO_MS) : 1;

      // ---- Fighters ------------------------------------------------------------------------
      const drawFighter = (b: BattleMon, own: boolean) => {
        const faint = reduced && isFaint(b.mon) ? 1 : b.faint;
        if (faint >= 1) return;
        if (!own && (captured || phase === "catch")) return;
        if (!own && trainer && phase === "intro" && !reduced && intro < INTRO_MS * 0.75) return;
        const pose = attack?.user === b && !reduced ? attackPose(attack) : { x: 0, y: 0 };
        const shake = b.shake > 0 && !reduced ? Math.round(Math.sin(b.shake / 18) * 4) : 0;
        const scale = own ? 3 : 2;
        const src = creatureSprite(b.mon.speciesId, own ? "back" : "front", scale, !!b.mon.shiny);
        const size = 32 * scale;
        const pad = own ? YOU_PAD : FOE_PAD;
        const enter = reduced ? 1 : easeOut(slide);
        const slideX = own ? Math.round((1 - enter) * 260) : Math.round((1 - enter) * -300);
        const breathe = !reduced && (phase === "command" || phase === "moves" || phase === "text" || phase === "tactics" || phase === "pack" || phase === "crew")
          ? Math.sin(now / (own ? 520 : 460) + (own ? 0 : 1.7)) : 0;
        const sink = Math.round(faint * size * 0.6);
        const dw = Math.round(size * (1 - breathe * 0.012));
        const dh = Math.round(size * (1 + breathe * 0.022)) - sink;
        const feetY = pad.y + (own ? 2 : 1) + pose.y;
        const x = Math.round(pad.x - dw / 2) + slideX + shake + pose.x;
        const shadowK = 1 - faint;
        g.ellipse(pad.x + slideX + pose.x * 0.3 + 4, pad.y + 2, (own ? 34 : 24) * shadowK, (own ? 7 : 5) * shadowK, [20, 16, 40, 90], 0.5);
        const flash = b.flash > 0 && !reduced;
        const frozen = b.status === "frost" && faint === 0;
        // Lag stutters the sprite sideways for a frame every so often.
        const glitch = b.status === "lag" && !reduced && Math.floor(now / 70) % 13 === 0 ? 3 : 0;
        g.setClip(0, 0, 384, feetY + 1);
        g.blitScaled(flash ? hitSprite(src) : src, x + glitch, feetY - dh - (own ? 2 : 1) + sink, dw, Math.round(size * (1 + breathe * 0.022)), {
          alpha: 1 - faint * 0.6,
          tint: faint > 0 ? [80, 70, 120, 255] : frozen ? [150, 215, 255, 255] : undefined,
          tintAmt: faint > 0 ? faint * 0.7 : frozen ? 0.38 : 0,
        });
        g.resetClip();
        if (b.status && faint === 0) drawStatusFx(g, b.status, pad.x + slideX + pose.x, feetY, size, now, reduced);
        if (b.mon.shiny && faint === 0) {
          // Shiny: gold sparkles twinkling around the body.
          for (let k = 0; k < 3; k++) {
            const tw = reduced ? 1 : Math.sin(now / 220 + k * 2.1);
            if (tw < 0.2) continue;
            const ang = (reduced ? 0 : now / 1400) + k * 2.09;
            const sx = Math.round(pad.x + slideX + pose.x + Math.cos(ang) * size * 0.42);
            const sy = Math.round(feetY - size * 0.5 + Math.sin(ang) * size * 0.3);
            g.text(sx - 2, sy - 3, "✦", withA(UI.goldHi, Math.round(255 * Math.min(1, tw))));
          }
        }
        if (faint > 0 && faint < 1 && !reduced) {
          for (let i = 0; i < 6; i++) {
            const a = (i / 6) * Math.PI;
            g.disc(pad.x + Math.cos(a) * (18 + faint * 20), pad.y - Math.sin(a) * 6 - faint * 10, 3 * (1 - faint), [240, 230, 210, 160]);
          }
        }
      };
      if ((trainer || pvp) && phase === "intro" && !reduced) {
        const k = Math.min(1, intro / (INTRO_MS * 0.55));
        const out = Math.max(0, (intro - INTRO_MS * 0.7) / (INTRO_MS * 0.3));
        const img = trainer ? actorFrame(`tr:${trainer.id}`, TRAINER_OUTFITS[trainer.id], 1, 0)
          : actorFrame(`peer:${pvp!.info.foe.hue}:0`, peerOutfit(pvp!.info.foe.hue, false), 1, 0);
        const tx = Math.round(FOE_PAD.x - 24 - (1 - easeOut(k)) * 200 + out * 90);
        g.ellipse(FOE_PAD.x + 4 - (1 - easeOut(k)) * 200 + out * 90, FOE_PAD.y + 2, 18, 4, [20, 16, 40, 90], 0.5);
        g.blit(img, tx, FOE_PAD.y - 76, { scale: 3, alpha: 1 - out });
      }
      if (attack?.user === foe) {
        if (you) drawFighter(you, true);
        if (foe) drawFighter(foe, false);
      } else {
        if (foe) drawFighter(foe, false);
        if (you) drawFighter(you, true);
      }
      if (attack && !reduced) {
        drawAttack(g, attack);
        const { impact, duration } = attackTiming(attack.move);
        const r = attack.result;
        if (r && r.hit && r.dmg > 0 && attack.elapsed >= impact) {
          const k = 1 - (attack.elapsed - impact) / Math.max(1, duration - impact);
          const strong = r.crit || attackStyle(attack.move) === "slam" || r.notes.some((n: string) => /Super/i.test(n));
          const amp = (strong ? 4 : 2) * Math.max(0, k) ** 2;
          g.shakeX = Math.sin(attack.elapsed / 16) * amp;
          g.shakeY = Math.cos(attack.elapsed / 21) * amp * 0.6;
        }
      }

      // ---- Plates --------------------------------------------------------------------------
      const plateT = phase === "intro" ? Math.max(0, (intro - SLIDE_MS * 0.6) / (INTRO_MS - SLIDE_MS * 0.6)) : 1;
      const showPlates = phase !== "catch" || !reduced;
      const rival = trainer?.boss || pvp?.info.raid ? "BOSS" as const : !!trainer || !!pvp;
      if (foe && showPlates && !(phase === "catch" && catchT > THROW_MS * 0.7)) foePlate(g, foe, rival, reduced ? 1 : plateT);
      else if (foe && showPlates) foePlate(g, foe, rival, 1);
      if (you) youPlate(g, you, reduced ? 1 : plateT);
      if (trainer) {
        const n = trainer.team.length;
        const out = partnerIndex + (foe && isFaint(foe.mon) ? 1 : 0);
        for (let i = 0; i < n; i++) {
          const tx = 12 + i * 12, ty = 54;
          g.blit(tinSprite(trainer.boss ? "myth" : "capsule"), tx, ty, { alpha: i < out ? 0.35 : 1, tint: i < out ? [60, 60, 80, 255] : undefined, tintAmt: 0.8 });
        }
        g.textShadow(36 + (n - 1) * 12, 60, `${trainer.boss ? "TRIAL" : "TEAM"} ${partnerIndex + 1}/${n}`, UI.textDim, UI.ink);
      } else if (pvp?.info.raid) {
        const raid = pvp.info.raid;
        const label = `RAID POOL ${raid.hp.toLocaleString("en-US")}/${raid.max.toLocaleString("en-US")}  ${raid.fighters} fighting`;
        glass(g, 8, 54, measureText(label) + 14, 14, { r: 6, alpha: 220 });
        g.text(15, 58, label, UI.pink);
      } else if (pvp) {
        const n = pvp.foeTeam.length;
        pvp.foeTeam.forEach((m, i) => {
          const down = isFaint(m) && (i !== pvp!.foeIndex || foe.faint >= 1);
          g.blit(tinSprite("gloss"), 12 + i * 12, 54, { alpha: down ? 0.35 : 1, tint: down ? [60, 60, 80, 255] : undefined, tintAmt: 0.8 });
        });
        g.textShadow(36 + (n - 1) * 12, 60, `${pvp.info.foe.name}  ${pvp.info.foe.rating}`, UI.textDim, UI.ink);
      } else if (phase === "pack") {
        const item = packItems()[packI]?.id;
        if (item && ITEMS[item].kind === "capsule") {
          const pct = `CATCH ${Math.round(catchChance(foe.mon, item, foe.status) * 100)}%`;
          glass(g, 12, 54, measureText(pct) + 30, 16, { r: 7 });
          g.blit(tinSprite(item === "myth" ? "myth" : item === "gloss" ? "gloss" : "capsule"), 13, 51);
          g.textShadow(33, 58, pct, UI.goldHi, UI.ink);
        }
      }

      // ---- Deck ----------------------------------------------------------------------------
      if (phase === "learn" && learnOffer) {
        const mv = moveById(learnOffer.moveId);
        card(g, 60, 40, 264, 110, "NEW MOVE", menuT, UI.gold);
        g.textShadow(74, 66, `${learnOffer.mon.nick} wants to learn`, UI.text, UI.ink);
        const bw = typeBadge(g, 74, 82, mv.type);
        g.textShadow(80 + bw, 84, mv.name, UI.goldHi, UI.ink);
        g.text(74, 100, `${mv.power ? `POW ${mv.power}` : "STATUS"}  ACC ${mv.acc}%  PP ${mv.pp}`, UI.textDim);
        const opts = ["LEARN", "SKIP"];
        opts.forEach((o, n) => {
          const bx = 74 + n * 90, by = 118;
          const on = n === learnChoice;
          g.rrect(bx, by, 80, 20, 6, on ? UI.gold : UI.bg2);
          g.text(bx + 10, by + 7, `${on ? ">" : " "}${o}`, on ? UI.paperInk : UI.text);
          if (on) focus(g, bx - 1, by - 1, 82, 22, now, reduced, 7);
        });
        g.text(260, 125, "A choose  B skip", UI.textMute);
        return;
      }

      if (phase === "forget" && learnOffer) {
        const mv = moveById(learnOffer.moveId);
        card(g, 60, 30, 264, 136, "FORGET A MOVE", menuT, UI.pink);
        g.textShadow(74, 56, `Forget which for ${mv.name}?`, UI.text, UI.ink);
        learnOffer.mon.moves.forEach((slot, n) => {
          const y = 74 + n * 18;
          const on = n === forgetI;
          const m = moveById(slot.id);
          if (on) { g.rrect(70, y - 4, 244, 16, 5, withA(UI.gold, 50)); g.rrectLine(70, y - 4, 244, 16, 5, UI.gold); }
          typeBadge(g, 76, y - 2, m.type, true);
          g.text(118, y, `${on ? ">" : " "}${m.name}`, on ? UI.goldHi : UI.text);
          g.textR(306, y, `PP ${slot.pp}/${m.pp}`, UI.textDim);
        });
        g.text(214, 150, "A forget  B back", UI.textMute);
        return;
      }

      if (phase === "tactics") {
        drawTactics(ctx, you, foe, guardBreakHint(you, foe, crew()) ?? attackPpHint(you, crew()) ?? statDropHint(you, foe), menuT);
      } else if (phase === "concede" && pvp) {
        card(g, 70, 50, 244, 100, pvp.info.raid ? "LEAVE THE RAID?" : "FORFEIT THIS DUEL?", menuT, UI.red);
        g.textShadow(84, 76, pvp.info.raid ? "Your hits so far still count." : `${pvp.info.foe.name} takes the win.`, UI.text, UI.ink);
        g.text(84, 92, pvp.info.raid ? "You can rejoin at the Raid Gate." : "No coins lost. Rating drops.", UI.textDim);
        hint(g, 84, 124, "B", "keep fighting", "b");
        hint(g, 212, 124, "A", "forfeit", "a");
      } else if (phase === "netwait" && pvp) {
        const left = Math.max(0, Math.ceil((pvp.deadline - now) / 1000));
        messageDeck(g, pvp.info.raid ? "The boss answers..." : pvp.foeReady || pvp.over ? `${pvp.info.foe.name} is ready. Resolving...` : `Waiting for ${pvp.info.foe.name}... ${left}s`, false, now, reduced, pvp.info.raid ? "RAID" : "LIVE DUEL");
      } else if (phase === "concede") {
        card(g, 70, 50, 244, 100, "CONCEDE THIS DUEL?", menuT, UI.red);
        g.textShadow(84, 76, "Return to Cope Clinic.", UI.text, UI.ink);
        g.text(84, 92, `Lose ${lossPenalty()} coins. No duel prize.`, UI.textDim);
        hint(g, 84, 124, "B", "keep fighting", "b");
        hint(g, 212, 124, "A", "concede", "a");
      } else if (phase === "intro") {
        if (introT > 0.5 || reduced) messageDeck(g, trainer ? `${trainer.name} steps forward!` : pvp ? `${pvp.info.foe.name} steps up!` : "The reeds picked a fight.", false, now, reduced, pvp ? "LIVE DUEL" : "BATTLE");
      } else if (phase === "attack" && attack) {
        messageDeck(g, attack.lines[0] ?? `${attack.user.mon.nick} uses ${attack.move.name}!`, false, now, reduced, attack.move.name.toUpperCase());
      } else if (phase === "text") {
        messageDeck(g, shown, waiting && textT >= textHold && (textHold === 0 || settled()), now, reduced);
      } else if (phase === "command") {
        commandDeck(g, pvp ? `vs ${pvp.info.foe.name}` : trainer ? trainer.name : "What now?",
          pvp ? `Turn ${pvp.turn + 1}  ${Math.max(0, Math.ceil((pvp.deadline - now) / 1000))}s`
            : attackPpHint(you, ctx.state.party) ? "No attack PP" : `${ctx.state.money} coins`, cmd, now, reduced, menuT);
      } else if (phase === "moves") {
        const slots = usableMoves(you.mon);
        const rows = slots.length ? slots : [{ id: "flailPost", pp: 1 }];
        const list: MoveRow[] = rows.map((slot) => {
          const def = moveDef(slot);
          const live = you.mon.moves.find((m) => m.id === def.id);
          return { name: def.name, type: def.type, pp: live?.pp ?? 0, max: def.pp };
        });
        const { cardX, cardY } = moveDeck(g, list, moveI, now, reduced, menuT);
        const slot = rows[moveI] ?? rows[0]!;
        const mv = moveDef(slot);
        const live = you.mon.moves.find((m) => m.id === mv.id);
        typeBadge(g, cardX + 7, cardY + 6, mv.type, true);
        g.textR(cardX + 114, cardY + 8, mv.power ? `POW ${mv.power}` : "STATUS", UI.text);
        g.text(cardX + 8, cardY + 20, `PP ${(live?.pp ?? 0)}/${mv.pp}`, UI.textDim);
        g.textR(cardX + 114, cardY + 20, `ACC ${you.sureHit ? 100 : mv.acc}%`, UI.textDim);
        g.text(cardX + 8, cardY + 32, fitText(moveEffectLabel(mv), 108), UI.textMute);
        if (mv.power > 0 && foe) {
          const mod = typeMod(mv.type, speciesById(foe.mon.speciesId).types);
          const flash = previewFlash > 0 && previewAt === moveI;
          if (mod > 1) {
            g.rrect(cardX + 58, cardY - 7, 58, 12, 5, flash ? UI.goldHi : UI.gold);
            g.text(cardX + 64, cardY - 5, "SUPER", UI.paperInk);
          } else if (mod > 0 && mod < 1) {
            g.rrect(cardX + 58, cardY - 7, 58, 12, 5, UI.bg3);
            g.text(cardX + 62, cardY - 5, "RESIST", UI.textDim);
          }
        }
      } else if (phase === "pack") {
        const items = packItems();
        if (!items.length) messageDeck(g, "Pack is empty.", true, now, reduced);
        else listDeck(g, items.map((it) => `${ITEMS[it.id].name} x${it.n}`), packI, now, reduced, menuT, undefined, "PACK");
      } else if (phase === "crew") {
        listDeck(g, crew().map((m) => `${m.nick}  ${m.hp}/${maxHp(m)}`), crewI, now, reduced, menuT,
          crew().map((m) => m.hp / maxHp(m)), "CREW");
      } else if (phase === "catch") {
        drawCatch(ctx, foe, catchT, shakes, catchOk, catchItem);
      }

      if (phase === "intro" && !reduced && introT < 0.5) {
        // Bars retract from the encounter wipe.
        const k = easeOut(introT / 0.5);
        const bars = 12;
        for (let i = 0; i < bars; i++) {
          const hBar = Math.ceil(216 / bars) + 1;
          const w = Math.round(384 * (1 - k));
          const y = Math.floor(i * (216 / bars));
          if (i % 2) g.fill(384 - w, y, w, hBar, [10, 8, 22, 255]);
          else g.fill(0, y, w, hBar, [10, 8, 22, 255]);
        }
      }
      if (leaving && leaveT > 0 && !reduced) {
        const a = Math.round(255 * (1 - Math.max(0, leaveT) / LEAVE_MS) ** 1.5);
        g.fill(0, 0, 384, 216, [8, 8, 18, a]);
      }
    },
  };
}

/** Small looping cues on an afflicted fighter; reduced motion freezes them in place. */
function drawStatusFx(g: Ctx["g"], s: StatusId, cx: number, feetY: number, size: number, now: number, reduced: boolean): void {
  const col = STATUS_META[s].col;
  const t = reduced ? 0 : now;
  const top = feetY - size;
  if (s === "scorch") {
    for (let i = 0; i < 3; i++) {
      const k = ((t / 700 + i / 3) % 1);
      const fx = Math.round(cx - size * 0.28 + i * size * 0.28 + Math.sin(t / 120 + i) * 2);
      const fy = Math.round(feetY - size * 0.35 - k * size * 0.45);
      const r = Math.max(1, Math.round(4 * (1 - k)));
      g.disc(fx, fy, r + 1, withA(col, 150));
      g.disc(fx, fy, r, [255, 220, 110, 230]);
    }
  } else if (s === "frost") {
    for (let i = 0; i < 5; i++) {
      const fx = Math.round(cx - size * 0.4 + i * size * 0.2), fy = feetY - 3 - (i % 2) * 4;
      g.fill(fx, fy - 5, 1, 7, [230, 250, 255, 220]);
      g.fill(fx - 2, fy - 2, 5, 1, [230, 250, 255, 220]);
      g.fill(fx - 1, fy - 3, 3, 3, withA(col, 160));
    }
  } else if (s === "afk") {
    for (let i = 0; i < 3; i++) {
      const k = ((t / 1400 + i / 3) % 1);
      g.textShadow(Math.round(cx + size * 0.28 + k * 10), Math.round(top + size * 0.15 - k * 16), i === 1 ? "Z" : "z", withA(col, Math.round(255 * (1 - k * 0.6))), UI.ink);
    }
  } else if (s === "fud") {
    for (let i = 0; i < 4; i++) {
      const k = ((t / 1100 + i / 4) % 1);
      const fx = Math.round(cx - size * 0.3 + i * size * 0.2 + Math.sin(t / 200 + i * 2) * 3);
      const fy = Math.round(feetY - size * 0.2 - k * size * 0.6);
      g.disc(fx, fy, 2 + (i % 2), withA(col, Math.round(200 * (1 - k))));
    }
  } else if (s === "lag") {
    // A four-bar signal meter with bars dropping out.
    const bars = reduced ? 1 : 1 + (Math.floor(now / 260) % 3);
    const bx = Math.round(cx + size * 0.22), by = Math.round(top + size * 0.12);
    for (let i = 0; i < 4; i++) {
      g.fill(bx + i * 3, by - i * 2, 2, 3 + i * 2, i < bars ? col : [60, 60, 80, 200]);
    }
  }
}

function drawTactics(ctx: Ctx, you: BattleMon, foe: BattleMon, actionHint: string | null, t: number): void {
  const g = ctx.g;
  const sp = speciesById(foe.mon.speciesId);
  const best = bestTypesAgainst(sp.types);
  const steps = (b: BattleMon) => (["atk", "def", "spd"] as const)
    .map((stat) => `${stat.toUpperCase()} ${b[stat] > 0 ? "+" : ""}${b[stat]}`).join("  ");
  card(g, 16, 10, 352, 196, "TACTICS", t, UI.teal);
  g.textR(360, 16, "A/B back", UI.textDim);
  g.textShadow(28, 36, `FOE  ${foe.mon.nick}`, UI.text, UI.ink);
  let bx = 28;
  for (const type of sp.types) bx += typeBadge(g, bx, 48, type) + 3;
  g.text(bx + 4, 50, sp.types.map((type) => TYPE_META[type].name).join(" / "), UI.textMute);
  g.text(28, 64, steps(foe), UI.text);
  if (foe.status) {
    const sx = 40 + measureText(steps(foe));
    g.text(sx, 64, fitText(`${STATUS_META[foe.status].name}: ${STATUS_META[foe.status].rule}`, 356 - sx), STATUS_META[foe.status].col);
  }
  g.textShadow(28, 78, best.multiplier > 1
    ? `Try ${best.types.map((type) => TYPE_META[type].chip).join("/")} x${best.multiplier}`
    : "No type has extra teeth here.", UI.gold, UI.ink);
  g.hline(28, 94, 328, withA(UI.line, 200));
  g.textShadow(28, 102, `YOU  ${you.mon.nick}`, UI.text, UI.ink);
  g.text(28, 116, steps(you), UI.text);
  if (you.status) {
    const sx = 40 + measureText(steps(you));
    g.text(sx, 116, fitText(`${STATUS_META[you.status].name}: ${STATUS_META[you.status].rule}`, 356 - sx), STATUS_META[you.status].col);
  }
  g.text(28, 130, "Switch clears steps and status; costs a turn.", UI.textDim);
  g.rrect(24, 146, 336, 50, 6, [10, 12, 26, 200]);
  g.para(32, 152, actionHint ?? "ATK power / DEF guard / SPD speed", 320, UI.teal, 11, 2);
  g.text(32, 180, "Reading tactics costs no turn.", UI.textMute);
}

function drawCatch(ctx: Ctx, foe: BattleMon, catchT: number, shakes: number, ok: boolean, item: ItemId): void {
  const g = ctx.g;
  const reduced = ctx.reduced;
  const kind: TinKind = item === "myth" ? "myth" : item === "gloss" ? "gloss" : "capsule";
  const sx = 70, sy = 150;
  const hx = FOE_PAD.x - 18, hy = 30;
  const gx = FOE_PAD.x - 18, gy = FOE_PAD.y - 42;
  const resultAt = THROW_MS + shakes * SHAKE_MS;
  const fly = THROW_MS * 0.45, absorb = THROW_MS * 0.72;
  let tx = gx, ty = gy, open = false;
  if (!reduced && catchT < fly) {
    const p = catchT / fly;
    tx = sx + (hx - sx) * p;
    ty = sy + (hy - sy) * p - Math.sin(p * Math.PI) * 60;
  } else if (!reduced && catchT < absorb) {
    tx = hx; ty = hy; open = true;
    const p = (catchT - fly) / (absorb - fly);
    // Beam and the foe shrinking into the tin.
    g.glow(hx + 18, hy + 16, 36 * (1 - p) + 10, [255, 120, 140, 255], 1.1);
    const src = creatureSprite(foe.mon.speciesId, "front", 2, !!foe.mon.shiny);
    const k = 1 - p;
    const w = Math.max(1, Math.round(64 * k)), h = Math.max(1, Math.round(64 * k));
    const cx = FOE_PAD.x + (hx + 18 - FOE_PAD.x) * p, cy = FOE_PAD.y - 30 + (hy + 16 - FOE_PAD.y + 30) * p;
    g.blitScaled(src, cx - w / 2, cy - h / 2, w, h, { tint: [255, 90, 120, 255], tintAmt: 0.4 + p * 0.6, alpha: 1 - p * 0.4 });
  } else if (!reduced && catchT < THROW_MS) {
    const p = (catchT - absorb) / (THROW_MS - absorb);
    tx = hx; ty = hy + (gy - hy) * Math.min(1, p * 1.4);
    if (p > 0.7) ty = gy - Math.sin(((p - 0.7) / 0.3) * Math.PI) * 10;
  }
  // Wobble during each latch.
  let wob = 0;
  if (!reduced && catchT >= THROW_MS && catchT < resultAt) {
    const e = catchT - THROW_MS;
    const beat = e % SHAKE_MS;
    const win = SHAKE_MS * 0.55;
    if (beat < win) wob = Math.sin((beat / win) * Math.PI * 2) * 4;
    if (beat > SHAKE_MS * 0.8 && beat < SHAKE_MS * 0.95) g.glow(tx + 18, ty + 8, 16, [255, 240, 180, 255], 0.8);
  }
  const done = catchT >= resultAt;
  if (done && !ok && !reduced) open = true;
  g.ellipse(tx + 19, gy + 42, 14, 3.5, [20, 16, 40, 110], 0.5);
  const img = tinSprite(kind, open);
  const spin = !reduced && catchT < fly ? Math.floor(catchT / 60) % 2 === 1 : false;
  const dw = 36 - Math.round(Math.abs(wob) * 1.2), dh = 44 + Math.round(Math.abs(wob) * 0.6);
  g.blitScaled(img, tx + wob + (36 - dw) / 2, ty - (dh - 44), dw, dh, { flipX: spin || wob < 0 });
  if (done && !reduced) {
    const e = Math.min(1, (catchT - resultAt) / SETTLE_MS);
    if (ok) {
      g.glow(tx + 18, ty + 16, 34 * (1 - e) + 8, [255, 230, 140, 255], 1);
      for (let i = 0; i < 16; i++) {
        const a = hash2(i, 1, 9) * Math.PI * 2;
        const d = 10 + e * (30 + hash2(i, 2, 9) * 30);
        const px = tx + 18 + Math.cos(a) * d, py = ty + 16 + Math.sin(a) * d * 0.7 + e * e * 30;
        const c = [UI.gold, UI.pink, UI.teal, UI.white][i % 4]!;
        g.fill(Math.round(px), Math.round(py), 2, 2, withA(c, 255 * (1 - e)));
      }
    } else {
      g.glow(tx + 18, ty + 10, 44 * (1 - e), [255, 255, 255, 255], 1.3);
      const src = creatureSprite(foe.mon.speciesId, "front", 2, !!foe.mon.shiny);
      const k = Math.min(1, e * 2.2);
      const w = Math.max(1, Math.round(64 * k)), h = Math.max(1, Math.round(64 * k));
      g.blitScaled(src, FOE_PAD.x - w / 2, FOE_PAD.y + 1 - h, w, h, { tint: [255, 255, 255, 255], tintAmt: 1 - k });
    }
  }
  const n = Math.min(shakes, Math.floor(Math.max(0, catchT - THROW_MS) / SHAKE_MS) + 1);
  const line = catchT < THROW_MS ? "Tin out!" : done ? (ok ? "Sealed!" : "Broke!") : n ? `Latch ${n}/3...` : "Hold...";
  messageDeck(g, line, false, ctx.now, reduced, "CATCH");
  for (let i = 0; i < 3; i++) {
    const lit = !reduced && catchT >= THROW_MS + (i + 1) * SHAKE_MS * 0.9 && i < shakes;
    g.disc(340 + i * 12, 185, 4, lit ? UI.gold : [40, 44, 72, 255]);
    g.ring(340 + i * 12, 185, 4, 4, UI.ink, 1);
  }
}

function drawEvolution(ctx: Ctx, evolution: Evolution, t: number): void {
  const g = ctx.g;
  const reduced = ctx.reduced;
  g.gradV(0, 0, 384, 216, [30, 20, 60, 255], [8, 6, 20, 255]);
  const cx = 192, cy = 96;
  if (!reduced) {
    const rot = t / 900;
    for (let i = 0; i < 14; i++) {
      const a = rot + (i / 14) * Math.PI * 2;
      for (let r = 20; r < 260; r += 2) {
        const w = 0.09;
        for (const da of [-w / 2, 0, w / 2]) {
          const x = cx + Math.cos(a + da) * r, y = cy + Math.sin(a + da) * r * 0.8;
          g.add(x, y, [255, 210, 140, 255], 0.06 * (1 - r / 260));
        }
      }
    }
    g.glow(cx, cy, 90, [255, 200, 120, 255], 0.35 + 0.15 * Math.sin(t / 120));
  }
  const swapped = t >= EVO_SWAP;
  const id = swapped ? evolution.to : evolution.from;
  const src = creatureSprite(id, "front", 3);
  const flash = !reduced && t >= EVO_FLASH_LO && t < EVO_FLASH_HI;
  // Pulse between form and silhouette, quickening toward the swap.
  const freq = 260 - Math.min(1, t / EVO_SWAP) * 200;
  const sil = !reduced && !swapped && Math.floor(t / Math.max(40, freq)) % 2 === 1;
  const bob = reduced ? 0 : Math.round(Math.sin(t / 300) * 2);
  g.ellipse(cx + 4, cy + 46, 30, 6, [0, 0, 0, 120], 0.5);
  g.blit(sil || flash ? hitSprite(src) : src, cx - 48, cy - 48 + bob);
  if (flash) g.fill(0, 0, 384, 216, [255, 255, 255, Math.round(255 * (1 - Math.abs((t - (EVO_FLASH_LO + EVO_FLASH_HI) / 2) / ((EVO_FLASH_HI - EVO_FLASH_LO) / 2))))]);
  if (!reduced && swapped) {
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * Math.PI * 2 + t / 700;
      const r = 56 + Math.sin(t / 200 + i) * 6;
      const x = Math.round(cx + Math.cos(a) * r), y = Math.round(cy + Math.sin(a) * r * 0.8);
      g.fill(x - 2, y, 5, 1, UI.goldHi);
      g.fill(x, y - 2, 1, 5, UI.goldHi);
    }
  }
  g.textOutline(192 - Math.round(measureText("EVOLUTION", 2) / 2), 14, "EVOLUTION", UI.gold, UI.ink, 2);
  messageDeck(g, `${speciesById(evolution.from).name} is evolving!`, false, ctx.now, reduced, "EVOLUTION");
}

/** Join entries (with ", " by default) into as few message boxes as fit (three wrapped rows each). */
export function packLines(entries: string[], rows = 3, sep = ", "): string[] {
  const out: string[] = [];
  let cur = "";
  for (const e of entries) {
    const next = cur ? `${cur}${sep}${e}` : e;
    if (cur && wrapPx(next, DECK.w - 30).length > rows) {
      out.push(cur);
      cur = e;
    } else cur = next;
  }
  if (cur) out.push(cur);
  return out;
}
